import {
  FormControl,
  InputLabel,
  MenuItem,
  makeStyles,
  Paper,
  Popover,
  Select,
  TextField,
  Typography,
} from "@material-ui/core"
import AddIcon from "@mui/icons-material/Add"
import FilterListIcon from "@mui/icons-material/FilterList"
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import RemoveIcon from "@mui/icons-material/Remove"
import { Box, Button, createTheme } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { DesktopDatePicker, MobileDatePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { useMediaQuery } from "react-responsive"
import { DATA_TYPES } from "../../../util/filters/filterConstants"

const theme_bp = createTheme({
  breakpoints: {
    values: {
      sm: 600,
    },
  },
})

const useStyles = makeStyles({
  filterRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  formControl: {
    margin: 5,
    width: "100%",
  },
  resize: {
    [theme_bp.breakpoints.down("sm")]: {
      fontSize: 10,
    },
  },
  dates: {
    display: "flex",
  },
  space: {
    width: 10,
  },
  buttonDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 10,
  },
})

const getPrefilteredData = (showArchived, data) => {
  return showArchived
    ? data
    : data.filter(
        (d) =>
          !d || !d.insight || !d.insight.insight_status || d.insight.insight_status !== "archive",
      )
}

export default function CustomTableFilters(props) {
  const { filterManager, data, setFilteredData, showArchived } = props
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [activeFilters, setActiveFilters] = useState([])

  const isMobile = useMediaQuery({ query: "(max-width: 625px)" })

  const classes = useStyles()

  const toggleArrow = () => {
    return dropdownOpen ? <ArrowDownIcon /> : <ArrowRightIcon />
  }

  const handleFilterClick = (event) => {
    setDropdownOpen(true)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setDropdownOpen(false)
    setAnchorEl(null)
  }

  const handleFilterValueChange = (filterType, value) => {
    filterManager.setValue(filterType, value)
    setFilteredData(filterManager.filterData(getPrefilteredData(showArchived, data)))
  }

  const handleFilterDateChange = (filterType, value, isFrom) => {
    if (isFrom) {
      filterManager.setFrom(filterType, value.toDate().toDateString())
    } else {
      filterManager.setTo(filterType, value.toDate().toDateString())
    }
    setFilteredData(filterManager.filterData(getPrefilteredData(showArchived, data)))
  }

  const handleFilterOperatorChange = (filterType, operator) => {
    filterManager.setOperator(filterType, operator)
    setFilteredData(filterManager.filterData(getPrefilteredData(showArchived, data)))
  }

  const handleAdd = () => {
    setActiveFilters([...activeFilters, null])
  }

  const handleRemove = (index) => {
    if (activeFilters && activeFilters.length > index) {
      handleFilterValueChange(activeFilters[index].filterType, undefined)
      setActiveFilters([...activeFilters].splice(index, 1))
    }
  }

  const handleHeaderChange = (filterType, index) => {
    const newActiveFilters = [...activeFilters]
    newActiveFilters[index] = filterManager.getFilter(filterType)
    if (activeFilters[index]) {
      const oldFilterType = activeFilters[index].filterType
      filterManager.setValue(oldFilterType, undefined)
    }
    setActiveFilters(newActiveFilters)
  }

  const handleSaveClick = () => {
    filterManager.saveFilters()
  }

  const handleClearFilters = () => {
    filterManager.clearSavedFilters()
    setFilteredData(filterManager.filterData(getPrefilteredData(showArchived, data)))
    setActiveFilters([null])
  }

  useEffect(() => {
    if (filterManager) {
      const existingFilters = filterManager.getActiveFilters()
      if (existingFilters.length === 0) {
        existingFilters.push(null)
      }
      setFilteredData(filterManager.filterData(getPrefilteredData(showArchived, data)))
      setActiveFilters(existingFilters)
    }
  }, [filterManager, setFilteredData, data, showArchived])

  const getFilterInput = (filter) => {
    const operators = ["==", ">=", "<="]
    switch (filter?.dataType) {
      case DATA_TYPES.DATE:
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className={classes.dates}>
              {isMobile ? (
                <>
                  <MobileDatePicker
                    label="From"
                    inputFormat="YYYY-MM-DD"
                    value={filter.from ? dayjs(new Date(filter.from)) : dayjs(new Date())}
                    onChange={(value) => handleFilterDateChange(filter?.filterType, value, true)}
                    renderInput={(params) => (
                      <TextField {...params} InputProps={{ classes: { input: classes.resize } }} />
                    )}
                  />
                  <MobileDatePicker
                    label="To"
                    inputFormat="YYYY-MM-DD"
                    value={filter.to ? dayjs(new Date(filter.to)) : dayjs(new Date())}
                    onChange={(value) => handleFilterDateChange(filter?.filterType, value, false)}
                    renderInput={(params) => (
                      <TextField {...params} InputProps={{ classes: { input: classes.resize } }} />
                    )}
                  />
                </>
              ) : (
                <>
                  <DesktopDatePicker
                    label="From"
                    inputFormat="YYYY-MM-DD"
                    value={filter.from ? dayjs(new Date(filter.from)) : dayjs(new Date())}
                    onChange={(value) => handleFilterDateChange(filter?.filterType, value, true)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                  <div className={classes.space}></div>
                  <DesktopDatePicker
                    label="To"
                    inputFormat="YYYY-MM-DD"
                    value={filter.to ? dayjs(new Date(filter.to)) : dayjs(new Date())}
                    onChange={(value) => handleFilterDateChange(filter?.filterType, value, false)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </>
              )}
            </div>
          </LocalizationProvider>
        )
      case DATA_TYPES.NUMBER:
      case DATA_TYPES.PERCENT:
        return (
          <Box display="flex" gap={1} alignItems="center">
            <FormControl fullWidth variant="outlined">
              <InputLabel id="operator-label">Operator</InputLabel>
              <Select
                labelId="operator-label"
                value={filter?.operator || "=="}
                onChange={(event) =>
                  handleFilterOperatorChange(filter?.filterType, event.target.value)
                }
                label="Operator"
              >
                {operators.map((comp) => (
                  <MenuItem key={comp} value={comp}>
                    {comp}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              value={filter?.value || ""}
              label="Value"
              onChange={(event) => handleFilterValueChange(filter?.filterType, event.target.value)}
              InputProps={{
                classes: {
                  input: classes.resize,
                },
              }}
            />
          </Box>
        )
      default:
        return (
          <TextField
            value={filter?.value || ""}
            label="Value"
            onChange={(event) => handleFilterValueChange(filter?.filterType, event.target.value)}
            InputProps={{
              classes: {
                input: classes.resize,
              },
            }}
          />
        )
    }
  }

  const getFilterSelector = (filter, index) => {
    return (
      <div className={classes.filterRow}>
        <FormControl className={classes.formControl}>
          <InputLabel id={`header-dropdown-${index}`}>Header</InputLabel>
          <Select
            labelId={`header-dropdown-${index}`}
            value={filter ? filter.filterType : ""}
            label="Header"
            onChange={(event) => handleHeaderChange(event.target.value, index)}
            inputProps={{
              classes: {
                select: classes.resize,
              },
            }}
          >
            {filterManager.getFilterableFilters().map((filter) => {
              return (
                <MenuItem
                  key={filter.filterName}
                  value={filter.filterType}
                  disabled={activeFilters.includes(filter)}
                >
                  {filter.filterName}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        <FormControl className={classes.formControl}>{getFilterInput(filter)}</FormControl>
        {index === activeFilters.length - 1 ? (
          <IconButton
            onClick={() => handleAdd()}
            disabled={activeFilters.length === filterManager.filters.length}
          >
            <AddIcon />
          </IconButton>
        ) : (
          <IconButton onClick={() => handleRemove(index)}>
            <RemoveIcon />
          </IconButton>
        )}
      </div>
    )
  }

  return (
    <>
      <Button onClick={(event) => handleFilterClick(event)} variant="outlined">
        <FilterListIcon />
        <span>Filters</span>
        {toggleArrow()}
      </Button>
      <Popover
        open={dropdownOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableEnforceFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Paper variant="elevation">
          <Box sx={{ padding: 2 }}>
            <Typography>Filters</Typography>
            {activeFilters.map((filter, index) => getFilterSelector(filter, index))}
          </Box>
          <div className={classes.buttonDiv}>
            <Button onClick={() => handleSaveClick()} variant="outlined">
              Save Filters
            </Button>
            <Button onClick={() => handleClearFilters()} variant="outlined">
              Clear Filters
            </Button>
          </div>
        </Paper>
      </Popover>
    </>
  )
}
